import { ethers } from 'ethers'

/**
 * Generates a new random 12-word mnemonic phrase
 * @returns {string} The generated mnemonic phrase
 */
export const generateMnemonic = (): string => {
  try {
    // Create a random wallet with ethers v6
    const wallet = ethers.Wallet.createRandom()

    // Verify that the wallet has a valid mnemonic phrase
    if (!wallet.mnemonic || !wallet.mnemonic.phrase) {
      throw new Error(
        'Generated wallet does not contain a valid mnemonic phrase'
      )
    }

    const mnemonic = wallet.mnemonic.phrase

    // Validate that the generated mnemonic phrase is valid
    if (!ethers.Mnemonic.isValidMnemonic(mnemonic)) {
      throw new Error('Generated mnemonic phrase is not valid')
    }

    console.log(
      `[Wallet] New wallet created with ${mnemonic.split(' ').length} word phrase`
    )

    return mnemonic
  } catch (error) {
    console.error('Error generating mnemonic:', error)
    throw new Error('Failed to generate recovery phrase. Please try again.')
  }
}

/**
 * Validates a mnemonic phrase
 * @param {string} mnemonic - The mnemonic phrase to validate
 * @returns {boolean} True if valid, false otherwise
 */
export const validateMnemonic = (mnemonic: string): boolean => {
  try {
    const cleanMnemonic = mnemonic.trim().toLowerCase().replace(/\s+/g, ' ')
    return ethers.Mnemonic.isValidMnemonic(cleanMnemonic)
  } catch (error) {
    console.error('Error validating mnemonic:', error)
    return false
  }
}

/**
 * Creates a wallet from a mnemonic phrase
 * @param {string} mnemonic - The mnemonic phrase
 * @returns {object} Wallet data including address and private key
 */
export const createWalletFromMnemonic = (mnemonic: string) => {
  try {
    const cleanMnemonic = mnemonic.trim().toLowerCase().replace(/\s+/g, ' ')

    if (!ethers.Mnemonic.isValidMnemonic(cleanMnemonic)) {
      throw new Error('Invalid mnemonic phrase')
    }

    const wallet = ethers.HDNodeWallet.fromPhrase(cleanMnemonic)

    return {
      address: wallet.address,
      privateKey: wallet.privateKey,
      mnemonic: wallet.mnemonic?.phrase || cleanMnemonic
    }
  } catch (error) {
    console.error('Error creating wallet from mnemonic:', error)
    throw new Error('Failed to create wallet from recovery phrase')
  }
}

/**
 * Encrypts wallet data with a password
 * @param {object} walletData - The wallet data to encrypt
 * @param {string} password - The password to use for encryption
 * @returns {Promise<string>} The encrypted wallet JSON
 */
export const encryptWallet = async (
  walletData: { privateKey: string; mnemonic?: string },
  password: string
): Promise<string> => {
  try {
    const wallet = new ethers.Wallet(walletData.privateKey)
    const encryptedJson = await wallet.encrypt(password)
    return encryptedJson
  } catch (error) {
    console.error('Error encrypting wallet:', error)
    throw new Error('Failed to encrypt wallet')
  }
}

/**
 * Decrypts wallet data with a password
 * @param {string} encryptedJson - The encrypted wallet JSON
 * @param {string} password - The password to use for decryption
 * @returns {Promise<object>} The decrypted wallet data
 */
export const decryptWallet = async (
  encryptedJson: string,
  password: string
): Promise<{ address: string; privateKey: string; mnemonic?: string }> => {
  try {
    const wallet = await ethers.Wallet.fromEncryptedJson(
      encryptedJson,
      password
    );
    return {
      address: wallet.address,
      privateKey: wallet.privateKey,
      mnemonic: (wallet as any).mnemonic?.phrase
    }
  } catch (error) {
    console.error('Error decrypting wallet:', error)
    throw new Error('Failed to decrypt wallet. Incorrect password?')
  }
}
